import { useContext } from "react";
import { AppContext } from "../App";
import { getCurrentYear } from "../Helpers";
import logo from "../assets/logo.png";
import { FiMail, FiMapPin, FiFacebook, FiInstagram, FiLinkedin, FiYoutube, FiPhone } from "react-icons/fi";
import { FaXTwitter } from "react-icons/fa6";
import { HiOutlineArrowRight, HiOutlineArrowUp } from "react-icons/hi";

const companyInfo = {
  name: "Mirabel Suites",
  tagline: "Hospitality, property management, and travel support for memorable stays in Tanzania.",
  email: "info@mirabelsuites.com",
  emailLink: "mailto:info@mirabelsuites.com",
  phone1: "+255 784 946 604",
  phone1Link: "tel:+255784946604",
  phone2: "+255 768 972 915",
  address: "P.O. Box 15404, Arusha, Tanzania",
  mapLink: "https://www.google.com/maps/search/?api=1&query=P.O.Box+15404+Arusha+Tanzania"
};

export default function Footer() {
  const appContext = useContext(AppContext);

  const navLinks = [
    { label: "Home", item: "home" },
    { label: "About Us", item: "about_us" },
    { label: "Services", item: "services" },
    { label: "Contact", item: "contact_us" },
  ];

  const ecoLinks = [
    { label: "Property Leasing", item: "services" },
    { label: "Property Management", item: "services" },
    { label: "Corporate Accommodation", item: "services" },
    { label: "Travel Support", item: "services" },
  ];

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Libre+Franklin:wght@300;400;500;600;700;800;900&display=swap');

        :root {
          --sunflower-amber: #BC9132;
          --sunflower-light: #E8C547;
          --text-navy: #012243;
          --footer-bg: #050810;
          --footer-card: #0F1626;
          --border-subtle: rgba(255, 255, 255, 0.08);
          --font-corporate: 'Libre Franklin', sans-serif;
        }

        .dl-footer {
          background: var(--footer-bg);
          color: #F8FAFC;
          font-family: var(--font-corporate);
          position: relative;
          overflow: hidden;
          padding-top: 1px; /* Prevent margin collapse */
        }

        /* Top golden glow line */
        .dl-footer::before {
          content: '';
          position: absolute;
          top: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 80%;
          height: 1px;
          background: linear-gradient(90deg, transparent, var(--sunflower-amber), transparent);
          opacity: 0.6;
          z-index: 2;
        }

        /* Subtle background radial glow */
        .footer-glow {
          position: absolute;
          top: -300px;
          left: 50%;
          transform: translateX(-50%);
          width: 1000px;
          height: 600px;
          background: radial-gradient(circle, rgba(188, 145, 50, 0.06) 0%, transparent 70%);
          pointer-events: none;
          z-index: 0;
        }

        .footer-content {
          position: relative;
          z-index: 1;
          max-width: 1320px;
          margin: 0 auto;
          padding: 0 48px;
        }

        /* --- TOP NEWSLETTER CTA --- */
        .footer-top-cta {
          padding: 80px 0;
          border-bottom: 1px solid var(--border-subtle);
          text-align: center;
        }

        .footer-top-cta h3 {
          font-size: clamp(1.8rem, 4vw, 2.8rem);
          font-weight: 800;
          margin: 0 0 16px;
          letter-spacing: -0.02em;
          line-height: 1.1;
        }

        .footer-top-cta h3 .italic {
          font-style: italic;
          font-weight: 300;
          color: var(--sunflower-amber);
        }

        .footer-top-cta p {
          color: #94A3B8;
          font-size: 1.05rem;
          margin: 0 auto;
          max-width: 500px;
          line-height: 1.6;
        }

        .newsletter-form {
          display: flex;
          max-width: 520px;
          margin: 36px auto 0;
          background: var(--footer-card);
          border: 1px solid var(--border-subtle);
          border-radius: 100px;
          padding: 6px;
          transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .newsletter-form:focus-within {
          border-color: var(--sunflower-amber);
          box-shadow: 0 0 0 4px rgba(188, 145, 50, 0.1);
        }

        .newsletter-form input {
          flex: 1;
          background: transparent;
          border: none;
          outline: none;
          color: #fff;
          padding: 0 28px;
          font-family: var(--font-corporate);
          font-size: 0.9rem;
        }

        .newsletter-form input::placeholder {
          color: #64748B;
        }

        .newsletter-form button {
          padding: 14px 32px;
          background: var(--sunflower-amber);
          color: var(--text-navy);
          border: none;
          border-radius: 100px;
          font-family: var(--font-corporate);
          font-weight: 700;
          font-size: 0.85rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
          white-space: nowrap;
        }

        .newsletter-form button:hover {
          background: var(--sunflower-light);
          transform: scale(1.03);
          box-shadow: 0 10px 25px -5px rgba(188, 145, 50, 0.4);
        }

        /* --- MAIN GRID --- */
        .footer-main {
          padding: 80px 0;
          display: grid;
          grid-template-columns: 1.5fr 1fr 1fr 1.2fr;
          gap: 60px;
        }

        .footer-col h4 {
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.15em;
          color: var(--sunflower-amber);
          margin: 0 0 28px;
        }

        .footer-col ul {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .footer-link {
          color: #94A3B8;
          text-decoration: none;
          font-size: 0.95rem;
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
          font-family: var(--font-corporate);
          transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .footer-link:hover {
          color: #fff;
          transform: translateX(6px);
        }

        /* Brand Column */
        .brand-col img {
          height: 48px;
          margin-bottom: 24px;
          filter: brightness(0) invert(1); /* Ensures logo is white on dark bg */
          opacity: 0.9;
        }

        .brand-desc {
          color: #94A3B8;
          font-size: 0.95rem;
          line-height: 1.7;
          margin: 0 0 32px;
          max-width: 320px;
        }

        .social-links {
          display: flex;
          gap: 12px;
        }

        .social-links a {
          width: 42px;
          height: 42px;
          border-radius: 50%;
          border: 1px solid var(--border-subtle);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #94A3B8;
          font-size: 1.1rem;
          transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .social-links a:hover {
          background: var(--sunflower-amber);
          border-color: var(--sunflower-amber);
          color: var(--text-navy);
          transform: translateY(-4px);
          box-shadow: 0 10px 20px -5px rgba(188, 145, 50, 0.4);
        }

        /* --- BOTTOM BAR --- */
        .footer-bottom {
          border-top: 1px solid var(--border-subtle);
          padding: 32px 0;
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 0.85rem;
          color: #64748B;
        }

        .footer-bottom a {
          color: var(--sunflower-amber);
          text-decoration: none;
          font-weight: 600;
          transition: color 0.2s ease;
        }

        .footer-bottom a:hover {
          color: var(--sunflower-light);
        }

        .back-to-top {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: var(--footer-card);
          border: 1px solid var(--border-subtle);
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .back-to-top:hover {
          background: var(--sunflower-amber);
          color: var(--text-navy);
          border-color: var(--sunflower-amber);
          transform: translateY(-3px);
          box-shadow: 0 10px 20px -5px rgba(188, 145, 50, 0.3);
        }

        /* --- RESPONSIVE --- */
        @media (max-width: 1024px) {
          .footer-content { padding: 0 32px; }
          .footer-main { grid-template-columns: 1fr 1fr; gap: 48px; }
        }

        @media (max-width: 768px) {
          .footer-content { padding: 0 24px; }
          .footer-top-cta { padding: 60px 0; }
          .footer-main { grid-template-columns: 1fr; text-align: center; gap: 40px; padding: 60px 0; }
          .brand-desc { margin: 0 auto 32px; }
          .social-links { justify-content: center; }
          .footer-bottom { flex-direction: column; gap: 20px; text-align: center; }
          
          .newsletter-form { 
            flex-direction: column; 
            border-radius: 20px; 
            padding: 8px; 
          }
          .newsletter-form input { 
            text-align: center; 
            padding: 14px; 
          }
          .newsletter-form button { 
            width: 100%; 
            justify-content: center; 
            padding: 16px; 
            border-radius: 14px; 
          }
        }
      `}</style>

      <footer className="dl-footer">
        <div className="footer-glow" />
        
        <div className="footer-content">
          {/* 1. Top Newsletter CTA */}
          <div className="footer-top-cta">
            <h3>Join the Mirabel <span className="italic">experience</span></h3>
            <p>Stay updated on our accommodation offers, partnership opportunities, and guest services.</p>
            <form className="newsletter-form d-none" onSubmit={(e) => e.preventDefault()}>
              <input type="email" placeholder="Enter your email address" required />
              <button type="submit">
                Subscribe <HiOutlineArrowRight size={16} />
              </button>
            </form>
          </div>

          {/* 2. Main Grid */}
          <div className="footer-main">
            {/* Brand Identity */}
            <div className="footer-col brand-col">
              <img src={logo} alt="Mirabel Suites" />
              <p className="brand-desc">{companyInfo.tagline}</p>
              <div className="social-links">
                <a href="#" aria-label="Facebook"><FiFacebook /></a>
                <a href="#" aria-label="Instagram"><FiInstagram /></a>
                <a href="#" aria-label="Twitter"><FaXTwitter /></a>
                <a href="#" aria-label="LinkedIn"><FiLinkedin /></a>
                <a href="#" aria-label="YouTube"><FiYoutube /></a>
              </div>
            </div>

            {/* Navigation */}
            <div className="footer-col">
              <h4>Navigation</h4>
              <ul>
                {navLinks.map(link => (
                  <li key={link.item}>
                    <button onClick={() => appContext?.navTo?.({ item: link.item })} className="footer-link">
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Ecosystem Services */}
            <div className="footer-col">
              <h4>Ecosystem</h4>
              <ul>
                {ecoLinks.map((link, i) => (
                  <li key={i}>
                    <button onClick={() => appContext?.navTo?.({ item: link.item })} className="footer-link">
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div className="footer-col">
              <h4>Contact Registry</h4>
              <ul>
                <li>
                  <a href={companyInfo.mapLink} target="_blank" rel="noreferrer" className="footer-link">
                    <FiMapPin /> {companyInfo.address}
                  </a>
                </li>
                <li>
                  <a href={companyInfo.emailLink} className="footer-link">
                    <FiMail /> {companyInfo.email}
                  </a>
                </li>
                <li>
                  <a href={companyInfo.phone1Link} className="footer-link">
                    <FiPhone /> {companyInfo.phone1}
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* 3. Bottom Bar */}
          <div className="footer-bottom">
            <p className="mb-0">© {getCurrentYear()} Mirabel Suites. All rights reserved.</p>
            <button className="back-to-top" onClick={scrollToTop} aria-label="Back to top">
              <HiOutlineArrowUp size={18} />
            </button>
          </div>
        </div>
      </footer>
    </>
  );
}
